<template>
  <nested-layout v-if="integrated"></nested-layout>
  <router-view v-else></router-view>
</template>

<script>
import NestedLayout from "@/components/nested_layout.vue";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "MarshallTableApp",
  components: {
    NestedLayout,
  },
  computed: {
    ...mapGetters({
      timeSheetsCompact: "timeSheets/all",
      integrated: "getIntegrated",
      reloading: "navigation/getReloading",
      tokenExpired: "permissions/isTokenExpired",
    }),
  },
  methods: {
    ...mapActions({
      setTimeSheetsCompact: "timeSheets/setTimeSheetsCompact",
      setTimestampInterval: "timeSheets/setTimestampInterval",
      setPermissions: "permissions/setPermissions",
      setDayValue: "navigation/setDayValue",
      setTimeSheets: "navigation/setTimeSheets",
      setStartHole: "navigation/setStartHole",
      setStartHoleSort: "navigation/setStartHoleSort",
      setPresetDate: "navigation/setPresetDate",
      setFlightTimeLimit: "navigation/setFlightTimeLimit",
      setShowHomeClub: "navigation/setShowHomeClub",
      setSingleHoleMode: "navigation/setSingleHoleMode",
      setSelectedSingleHole: "navigation/setSelectedSingleHole",
      setReloading: "navigation/setReloading",
    }),
    async appSetup(reload = false) {
      if (this.loaded == false || reload === true) {
        await this.setPermissions(this.token);

        // expired link (NM-23504): skip loading any data, the layout shows an expiry message
        if (this.tokenExpired) {
          this.loaded = true;
          return;
        }

        await this.connectWebSocket();
        await this.setTimeSheetsCompact();
        this.setTimestampInterval();

        // initial navigation setup
        if (this.sheets) {
          this.sheets = this.sheets.split(",").map((id) => id);
          this.setTimeSheets(this.sheets);

          const firstTimeSheet = this.timeSheetsCompact.find(
            (tS) => tS.id == this.sheets[0]
          );
          const dayValue = firstTimeSheet?.date ?? null;
          this.setDayValue(dayValue);
        }
        if (!isNaN(this.startHole)) {
          this.setStartHole(this.startHole * 1);
        }
        if (!isNaN(this.startHoleSort)) {
          this.setStartHoleSort(this.startHoleSort * 1);
        }
        if (this.presetDate) {
          this.setPresetDate(this.presetDate + "T00:00:00Z");
        }
        if (!isNaN(this.flightTimeLimit)) {
          this.setFlightTimeLimit(this.flightTimeLimit * 1);
        }
        if (
          this.can("showHomeClub:default") ||
          localStorage.getItem("show_home_club") == "true"
        ) {
          this.setShowHomeClub(true);
        }
        if (this.singleHoleMode) {
          this.setSingleHoleMode(true);
        }
        if (this.selectedSingleHole && !isNaN(this.selectedSingleHole)) {
          this.setSelectedSingleHole(this.selectedSingleHole * 1);
        }
        this.loaded = true;
      }
    },
    setDataFromScriptTags() {
      this.token = document.currentScript?.getAttribute("token");
      this.sheets = document.currentScript?.getAttribute("sheets");
      this.startHole = document.currentScript?.getAttribute("startHole");
      this.startHoleSort =
        document.currentScript?.getAttribute("startHoleSort");
      this.presetDate = document.currentScript?.getAttribute("date");
      this.flightTimeLimit =
        document.currentScript?.getAttribute("flightTimeLimit");
      this.singleHoleMode =
        document.currentScript?.getAttribute("singleHoleMode");
      this.selectedSingleHole =
        document.currentScript?.getAttribute("selectedSingleHole");
    },
    setDataFromQueryParameters(to) {
      this.token = to.query.token ?? null;
      this.sheets = to.query.sheets ?? null;
      this.startHole = to.query.startHole ?? null;
      this.startHoleSort = to.query.startHoleSort ?? null;
      this.presetDate = to.query.date ?? null;
      this.flightTimeLimit = to.query.flightTimeLimit ?? null;
      this.singleHoleMode = to.query.singleHoleMode ?? null;
      this.selectedSingleHole = to.query.selectedSingleHole ?? null;
    },
  },
  data() {
    return {
      loaded: false,
      token: null,
      sheets: null,
      startHole: null,
      startHoleSort: null,
      presetDate: null,
      flightTimeLimit: null,
      singleHoleMode: null,
      selectedSingleHole: null,
    };
  },
  async created() {
    if (this.integrated) {
      this.setDataFromScriptTags();
      await this.appSetup();
    } else {
      this.$router.beforeEach(async (to, _, next) => {
        this.setDataFromQueryParameters(to);
        await this.appSetup();
        next();
      });
    }
  },
  watch: {
    async reloading(newVal) {
      if (newVal) {
        this.setDataFromQueryParameters(this.$route);
        await this.appSetup(true);
        this.setReloading(false);
      }
    },
  },
};
</script>

<style lang="scss">
#app {
  touch-action: manipulation;
}
</style>
